import { Download } from 'lucide-react';
import { useClientInvoices } from '../../hooks/useClients';
import { formatCurrency, formatDate } from '../../lib/format';

export function ClientInvoicesTab({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useClientInvoices(clientId);
  const invoices = data?.data ?? [];
  const totals = data?.meta.totals;

  if (isLoading) return <p className="text-sm text-gray-500">Se încarcă facturile…</p>;
  if (isError) return <p className="text-sm text-red-600">Facturile nu au putut fi încărcate.</p>;

  return (
    <div>
      {/* Phones: the three totals as rows of one card, not three tall cards
          stacked before the first invoice. */}
      {totals && (
        <dl className="mb-4 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white text-sm shadow-sm sm:hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500">Total facturat</dt>
            <dd className="font-semibold tabular-nums text-gray-900">
              {formatCurrency(totals.totalInvoiced, totals.currency)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500">Total încasat</dt>
            <dd className="font-semibold tabular-nums text-green-700">
              {formatCurrency(totals.totalPaid, totals.currency)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <dt className="text-gray-500">De încasat</dt>
            <dd
              className={`font-semibold tabular-nums ${totals.outstanding > 0 ? 'text-amber-700' : 'text-gray-500'}`}
            >
              {formatCurrency(totals.outstanding, totals.currency)}
            </dd>
          </div>
        </dl>
      )}
      {totals && (
        <div className="mb-4 hidden grid-cols-3 gap-3 sm:grid">
          <SummaryStat
            label="Total facturat"
            value={formatCurrency(totals.totalInvoiced, totals.currency)}
          />
          <SummaryStat
            label="Total încasat"
            value={formatCurrency(totals.totalPaid, totals.currency)}
            tone="text-green-700"
          />
          <SummaryStat
            label="De încasat"
            value={formatCurrency(totals.outstanding, totals.currency)}
            tone={totals.outstanding > 0 ? 'text-amber-700' : 'text-gray-500'}
          />
        </div>
      )}

      {invoices.length === 0 ? (
        <p className="py-8 text-center text-gray-500">Nicio factură asociată acestui client.</p>
      ) : (
        <>
          {/* Below lg: one card per invoice; the eight-column table squeezed
              the project name and dates into word-per-line columns. */}
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white lg:hidden">
            {invoices.map((inv) => (
              <li key={`${inv.source}-${inv.id}`} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{inv.invoiceNumber}</div>
                    <div className="truncate text-xs text-gray-500">{inv.project?.name ?? '—'}</div>
                  </div>
                  <div className="shrink-0 text-right font-semibold tabular-nums text-gray-900">
                    {formatCurrency(inv.amount, inv.currency)}
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 font-medium ${
                      inv.overdue
                        ? 'bg-red-100 text-red-700'
                        : inv.paidAmount >= inv.amount && inv.amount > 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {inv.overdue ? 'Restantă' : inv.status}
                  </span>
                  <span>
                    {formatDate(inv.issueDate)} → {formatDate(inv.dueDate)}
                  </span>
                  <span>· {inv.source === 'smartbill' ? 'SmartBill' : 'DB'}</span>
                  {inv.s3Url && (
                    <a
                      href={inv.s3Url}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto inline-flex items-center gap-1 text-gray-500 hover:text-brand-600"
                    >
                      <Download size={14} /> PDF
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto rounded-lg border border-gray-200 lg:block">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Număr</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Sursă</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Proiect</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Emisă</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Scadență</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Sumă</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {invoices.map((inv) => (
                  <tr key={`${inv.source}-${inv.id}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{inv.invoiceNumber}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          inv.source === 'smartbill'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {inv.source === 'smartbill' ? 'SmartBill' : 'DB'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{inv.project?.name ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDate(inv.issueDate)}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDate(inv.dueDate)}</td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatCurrency(inv.amount, inv.currency)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          inv.overdue
                            ? 'bg-red-100 text-red-700'
                            : inv.paidAmount >= inv.amount && inv.amount > 0
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {inv.overdue ? 'Restantă' : inv.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {inv.s3Url && (
                        <a
                          href={inv.s3Url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center text-gray-400 hover:text-brand-600"
                          aria-label="Descarcă PDF"
                        >
                          <Download size={15} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${tone ?? 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
